import {
    ColumnDefinitionsDictionary,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import {
    ColumnFilterState,
    FilterState,
    isColumnFilter,
    isRowFilter,
} from 'widgets/store/grid/filters/FilterState';
import {
    formatFilterValue,
    getOppositeBoundarySign,
    isExactFilter,
    isMembershipFilter,
    isRangeFilter,
    MembershipSign,
    signTextMap,
} from 'utils/filter/filter';
import { EditedColumnNames } from 'grid/columnNames/hooks';

import { textUtils } from '@instinet-analytics/core-lib';

const { pluralise } = textUtils;

export function filterToString<TColumnId extends string>(
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>,
    filterState: FilterState<TColumnId>,
    editedColumnNames?: EditedColumnNames
) {
    if (isColumnFilter(filterState)) {
        const columnName =
            (editedColumnNames ?? {})[filterState.column] ??
            columnDefinitions[filterState.column].name;
        const filter = filterState.filter;

        if (isExactFilter(filter)) {
            if (
                filter.valueType === ValueType.DATE ||
                filter.valueType === ValueType.DATETIME
            ) {
                return `${columnName} ${
                    signTextMap[filter.exactSign]
                } ${formatFilterValue(filter.valueType, filter.value)}`;
            }

            return `${columnName} ${
                signTextMap[filter.exactSign]
            } ${formatFilterValue(
                filter.valueType,
                filter.value,
                columnDefinitions[filterState.column].enumMap
            )}`;
        }

        if (isRangeFilter(filter)) {
            if (filter.lowerSign && filter.upperSign) {
                return `${formatFilterValue(
                    filter.valueType,
                    filter.lowerValue
                )} 
                    ${signTextMap[getOppositeBoundarySign(filter.lowerSign)]}
                    ${columnName} 
                    ${signTextMap[filter.upperSign]}
                    ${formatFilterValue(filter.valueType, filter.upperValue)}`;
            }

            if (filter.lowerSign) {
                return `${columnName} ${
                    signTextMap[filter.lowerSign]
                } ${formatFilterValue(filter.valueType, filter.lowerValue)}`;
            }

            if (filter.upperSign) {
                return `${columnName} ${
                    signTextMap[filter.upperSign]
                } ${formatFilterValue(filter.valueType, filter.upperValue)}`;
            }
        }

        if (isMembershipFilter(filter)) {
            return `${columnName} ${
                signTextMap[filter.membershipSign]
            } ${filter.values
                .map((f) =>
                    formatFilterValue(
                        filter.valueType,
                        f,
                        columnDefinitions[filterState.column].enumMap
                    )
                )
                .join(', ')}`;
        }
    }

    if (isRowFilter(filterState)) {
        const signText =
            filterState.sign === MembershipSign.INCLUDE
                ? 'Included'
                : 'Excluded';
        return `${signText}: ${filterState.rowIds.length} ${pluralise(
            filterState.rowIds.length,
            'order'
        )}`;
    }

    return '';
}

export function filtersToCommaDelimitedString<TColumnId extends string>(
    filters: FilterState<TColumnId>[],
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>,
    emptyText = '(None)'
) {
    if (filters.length === 0) {
        return emptyText;
    }
    return filters
        .map((f) =>
            filterToString(columnDefinitions, f as ColumnFilterState<TColumnId>)
        )
        .join(', ');
}
